import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Bundle inventory: main product + companion lines when main qty is 3+ and 4+.
 * All IDs are hardcoded. Cart `/add` requires variant id — if adds fail, paste variant IDs from Admin.
 */

const BUNDLE_MAIN = 8880387784926;
const BUNDLE_C3 = 9187229565150;
const BUNDLE_C4 = 9187229663454;
const BUNDLE_MAX_MAIN = 4;

const ADD_C3 = BUNDLE_C3;
const ADD_C4 = BUNDLE_C4;

const BUNDLE_EVENT_SOURCE = 'bundle-inventory-sync';

/**
 * @param {object} item
 * @param {number} id
 */
function matchesId(item, id) {
  return item.product_id === id || item.variant_id === id;
}

let syncing = false;
let pending = false;

/**
 * @returns {Promise<any>}
 */
async function fetchCartJson() {
  const res = await fetch(`${Theme.routes.cart_url}.js`);
  if (!res.ok) throw new Error('Cart fetch failed');
  return res.json();
}

/**
 * @param {Record<string, number>} updates
 * @param {{ mutated: boolean }} state
 */
async function postCartUpdates(updates, state) {
  if (!Object.keys(updates).length) return;
  const res = await fetch(Theme.routes.cart_update_url, {
    ...fetchConfig('json', { body: JSON.stringify({ updates }) }),
  });
  if (!res.ok) {
    console.error('[bundle-inventory] cart/update failed', await res.text());
    return;
  }
  state.mutated = true;
}

/**
 * @param {any[]} lines
 * @param {Record<string, number>} updates
 */
function zeroAllLines(lines, updates) {
  for (const line of lines) {
    if (line.quantity > 0) {
      updates[line.key] = 0;
    }
  }
}

/**
 * @param {any[]} lines
 * @param {number} desired
 * @param {Record<string, number>} updates
 */
function setCompanionQtyOnLines(lines, desired, updates) {
  if (lines.length === 0) return;
  const sorted = [...lines].sort((a, b) => a.line - b.line);
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      updates[sorted[i].key] = desired;
    } else if (sorted[i].quantity > 0) {
      updates[sorted[i].key] = 0;
    }
  }
}

/**
 * @param {any[]} c3Lines
 * @param {any[]} c4Lines
 * @param {Record<string, number>} updates
 */
function removeCompanionLines(c3Lines, c4Lines, updates) {
  zeroAllLines(c3Lines, updates);
  zeroAllLines(c4Lines, updates);
}

/**
 * @param {any[]} mainLines
 * @param {number} currentTotal
 * @param {number} max
 * @param {Record<string, number>} updates
 */
function capMainToMax(mainLines, currentTotal, max, updates) {
  let toRemove = currentTotal - max;
  const sorted = [...mainLines].sort((a, b) => a.line - b.line);
  for (let i = sorted.length - 1; i >= 0 && toRemove > 0; i--) {
    const line = sorted[i];
    const dec = Math.min(line.quantity, toRemove);
    if (dec > 0) {
      const next = line.quantity - dec;
      updates[line.key] = next;
      toRemove -= dec;
    }
  }
}

/**
 * @param {number} id
 * @param {any[]} items
 */
function sumForId(id, items) {
  return items
    .filter((i) => matchesId(i, id))
    .reduce((s, l) => s + l.quantity, 0);
}

/**
 * @param {number} variantId
 * @param {number} quantity
 * @param {{ mutated: boolean }} state
 */
async function addToCartByVariant(variantId, quantity, state) {
  if (quantity < 1) return;
  const res = await fetch(Theme.routes.cart_add_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties: { _bundle_inventory: '1' } }],
      }),
    }),
  });
  if (!res.ok) {
    console.error('[bundle-inventory] cart/add failed', variantId, await res.text());
    return;
  }
  state.mutated = true;
}

/**
 * @param {{ mutated: boolean }} state
 * @returns {Promise<boolean>} true if cart was changed
 */
async function runSync(state) {
  const cart = await fetchCartJson();
  const items = /** @type {any[]} */ (cart.items || []);

  const mainLines = items.filter((i) => matchesId(i, BUNDLE_MAIN));
  const c3Lines = items.filter((i) => matchesId(i, BUNDLE_C3));
  const c4Lines = items.filter((i) => matchesId(i, BUNDLE_C4));

  const mainTotal = mainLines.reduce((s, l) => s + l.quantity, 0);
  const c3Total = c3Lines.reduce((s, l) => s + l.quantity, 0);
  const c4Total = c4Lines.reduce((s, l) => s + l.quantity, 0);

  if (mainTotal === 0 && (c3Total > 0 || c4Total > 0)) {
    /** @type {Record<string, number>} */
    const u = {};
    removeCompanionLines(c3Lines, c4Lines, u);
    await postCartUpdates(u, state);
    return state.mutated;
  }

  if (mainTotal > BUNDLE_MAX_MAIN) {
    /** @type {Record<string, number>} */
    const u = {};
    capMainToMax(mainLines, mainTotal, BUNDLE_MAX_MAIN, u);
    if (Object.keys(u).length) {
      await postCartUpdates(u, state);
    }
  }

  const fresh = await fetchCartJson();
  const it = /** @type {any[]} */ (fresh.items || []);
  const mainL = it.filter((i) => matchesId(i, BUNDLE_MAIN));
  const c3L = it.filter((i) => matchesId(i, BUNDLE_C3));
  const c4L = it.filter((i) => matchesId(i, BUNDLE_C4));
  const m = mainL.reduce((s, l) => s + l.quantity, 0);
  const c3N = c3L.reduce((s, l) => s + l.quantity, 0);
  const c4N = c4L.reduce((s, l) => s + l.quantity, 0);

  const need3 = m >= 3 ? 1 : 0;
  const need4 = m >= 4 ? 1 : 0;

  if (c3N === need3 && c4N === need4) {
    return state.mutated;
  }

  /** @type {Record<string, number>} */
  const updates = {};

  if (need3 === 0) {
    zeroAllLines(c3L, updates);
  } else if (c3N > 1 || c3L.length > 1) {
    setCompanionQtyOnLines(c3L, 1, updates);
  }

  if (need4 === 0) {
    zeroAllLines(c4L, updates);
  } else if (c4N > 1 || c4L.length > 1) {
    setCompanionQtyOnLines(c4L, 1, updates);
  }

  if (Object.keys(updates).length) {
    await postCartUpdates(updates, state);
  }

  let cart3 = await fetchCartJson();
  if (need3 > 0 && sumForId(BUNDLE_C3, /** @type {any[]} */ (cart3.items || [])) < 1) {
    await addToCartByVariant(ADD_C3, 1, state);
    cart3 = await fetchCartJson();
  }
  if (need4 > 0 && sumForId(BUNDLE_C4, /** @type {any[]} */ (cart3.items || [])) < 1) {
    await addToCartByVariant(ADD_C4, 1, state);
  }

  return state.mutated;
}

/**
 * @param {any} cart
 */
function announceCart(cart) {
  const count = typeof cart?.item_count === 'number' ? cart.item_count : 0;
  document.dispatchEvent(
    new CartUpdateEvent(cart, BUNDLE_EVENT_SOURCE, { itemCount: count, source: BUNDLE_EVENT_SOURCE })
  );
}

export async function syncBundleInventory() {
  if (syncing) {
    pending = true;
    return;
  }
  syncing = true;
  try {
    const state = { mutated: false };
    await runSync(state);
    while (pending) {
      pending = false;
      await runSync(state);
    }
    if (state.mutated) {
      const cart = await fetchCartJson();
      announceCart(cart);
      const ids = new Set();
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
      });
      await Promise.all(Array.from(ids, (id) => sectionRenderer.renderSection(id, { cache: false })));
    }
  } catch (e) {
    console.error('[bundle-inventory]', e);
  } finally {
    syncing = false;
  }
}

/**
 * @param {Event} e
 */
function onCartEvent(e) {
  const src = e instanceof CustomEvent && e.detail && typeof e.detail === 'object' && 'data' in e.detail ? e.detail.data?.source : undefined;
  if (src === BUNDLE_EVENT_SOURCE) return;
  void syncBundleInventory();
}

const debounce = (fn, ms) => {
  let t;
  return (e) => {
    clearTimeout(t);
    t = setTimeout(() => fn(e), ms);
  };
};

const debounced = debounce(onCartEvent, 80);

if (typeof document !== 'undefined') {
  document.addEventListener(ThemeEvents.cartUpdate, debounced);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => onCartEvent(new Event('load')), { once: true });
  } else {
    onCartEvent(new Event('load'));
  }
}

if (typeof window !== 'undefined' && window.__BUNDLE_INVENTORY_PDP) {
  const setMax = () => {
    const form = document.querySelector('product-form');
    if (!form) return;
    const q =
      form.querySelector('#quantity') ?? form.querySelector('input[name="quantity"].quantity-input, input[name="quantity"]');
    if (q instanceof HTMLInputElement) {
      q.setAttribute('max', String(BUNDLE_MAX_MAIN));
      if (Number.parseInt(q.value, 10) > BUNDLE_MAX_MAIN) {
        q.value = String(BUNDLE_MAX_MAIN);
      }
    }
  };
  setMax();
  document.addEventListener(ThemeEvents.variantUpdate, setMax, { capture: true });
}
